import { AccountField } from 'components/shared/dropdowns'
import {
  AccountInteractionsDocument,
  AllTodosDocument,
  CreateInteractionMutationVariables,
  InteractionFilter,
  InteractionLogObjectType,
  InteractionTokenInput,
  InteractionType,
  InteractionWithRollupsDataFragment,
  OpportunityInteractionsDocument,
  UpdateInteractionMutationVariables,
  useAccountParentsQuery,
  useCreateInteractionMutation,
  useUpdateInteractionMutation,
} from 'graphql/bindings/AdminBindings'
import {
  Box,
  FlagField,
  FormModal,
  FormSectionTitle,
  SpinnerButton,
  ValueAutocompleteField,
} from '@forgeglobal/design-framework'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { TaggedConversation } from 'components/communication-input/CommunicationInput'
import { ValueJSON } from 'slate'
import { WrappedCommunicationInput } from 'components/shared/Communications'
import { contactMethods } from 'util/Enums'
import { titleCase } from 'util/StringUtils'
import React, { Fragment, useEffect, useMemo, useState } from 'react'

type FormType = 'create' | 'edit'

interface FormShape {
  id?: string
  roll_up_from_account?: { label: string; value: string }
  roll_up_to_organization_id?: string
  contact_method?: InteractionType
  communication?: TaggedConversation
  share_on_slack: boolean
  slack_channel?: string
}

type ConversationFormProps = FormRenderProps<FormShape> & {
  hideContactMethod?: boolean
  hideRollups?: boolean
  resourceName?: string
  accountId: string
  loading: boolean
  formType: FormType
}

const ConversationForm: React.FC<ConversationFormProps> = ({
  handleSubmit,
  form,
  values,
  valid,
  hideContactMethod,
  hideRollups,
  resourceName,
  accountId,
  loading,
  formType,
}) => {
  const [hack, setHack] = useState(0)
  const accountValue = values?.roll_up_from_account?.value ?? ''
  const { data } = useAccountParentsQuery({
    variables: { accountId: accountValue },
    skip: !accountValue,
    fetchPolicy: 'network-only',
  })
  const parentOptions = useMemo(() => {
    return (data?.account?.parents ?? [])
      .map(a => a.parent_account)
      .filter(a => a.type === 'organization')
      .map(a => ({ label: a.name, value: a.id }))
  }, [data])
  const changeForm = form.change
  useEffect(() => {
    if (parentOptions.length && formType === 'create') {
      changeForm('roll_up_to_organization_id', parentOptions[0].value)
      setHack(h => h + 1)
    }
  }, [changeForm, parentOptions, formType, setHack])
  return (
    <form onSubmit={handleSubmit}>
      {!hideRollups && (
        <Box marginTop={4}>
          <FormSectionTitle>Conversation with</FormSectionTitle>
          <AccountField name="roll_up_from_account" label="Name (required)" />
          {parentOptions.length > 0 && (
            <ValueAutocompleteField
              key={hack}
              name="roll_up_to_organization_id"
              label="Roll up to organization, if applicable"
              options={parentOptions}
            />
          )}
        </Box>
      )}
      <Box marginTop={4}>
        <FormSectionTitle>Notes</FormSectionTitle>
        {!hideContactMethod ? (
          <ValueAutocompleteField name="contact_method" label="Contact method (required)" options={contactMethods} />
        ) : (
          <Field name="contact_method">{fieldProps => <input hidden {...fieldProps.input} />}</Field>
        )}
        <WrappedCommunicationInput accountId={accountId} name="communication" label="Details" isEditorTall />
        {formType === 'create' && (
          <Fragment>
            <Field name="share_on_slack" label="Share on Slack?" component={FlagField} fullWidth />
            {values.share_on_slack && (
              <ValueAutocompleteField
                name="slack_channel"
                label="Channel"
                options={[
                  { value: 'marketplace', label: '#marketplace' },
                  { value: 'client_strategy', label: '#client_strategy' },
                  { value: 'marketplace_team', label: '#marketplace-team' },
                  { value: 'forge_capital', label: '#forge-capital' },
                ]}
              />
            )}
          </Fragment>
        )}
      </Box>
      <Box my={6}>
        <SpinnerButton
          variant="contained"
          color="primary"
          type="submit"
          disabled={!valid}
          fullWidth
          loading={loading}
          caption={`Log ${resourceName || 'Conversation'}`}
        />
      </Box>
    </form>
  )
}

const validateForm = (values: FormShape) => {
  let errors: { [key: string]: string } = {}
  if (!values.contact_method) {
    errors['contact_method'] = 'Contact method is required'
  }
  if (!values.roll_up_from_account) {
    errors['Name'] = 'Account name is required'
  }
  if ((values?.communication?.text ?? '').trim() === '') {
    errors['communication'] = 'Communication is required'
  }
  return errors
}

const createInitialValues = (
  formType: FormType,
  {
    isAccount,
    objId,
    accountId,
    accountName,
    conversation,
    defaultContactMethod,
  }: Pick<
    ConversationModalProps,
    'isAccount' | 'objId' | 'accountId' | 'accountName' | 'conversation' | 'defaultContactMethod'
  >
): FormShape => {
  const existingContactMethod: InteractionType = conversation?.type as InteractionType

  if (formType === 'create') {
    const rollupFromAccount: { label: string; value: string } = {
      label: accountName || '',
      value: '',
    }

    rollupFromAccount.label = accountName || ''

    if (isAccount) {
      rollupFromAccount.value = objId
    } else if (accountId) {
      rollupFromAccount.value = accountId
    }

    return {
      contact_method: defaultContactMethod || InteractionType.PhoneCall,
      slack_channel: 'client_strategy',
      share_on_slack: false,
      roll_up_from_account: rollupFromAccount.value ? rollupFromAccount : undefined,
    }
  }

  return {
    id: conversation?.id,
    contact_method: existingContactMethod,
    communication: {
      json: JSON.parse(conversation?.tagged_value) as ValueJSON,
      text: conversation?.notes ?? '',
      tokens: [],
    },
    share_on_slack: false,
    roll_up_from_account: {
      label: conversation?.rollup_from_account?.name ?? '',
      value: conversation?.rollup_from_account?.id ?? '',
    },
    roll_up_to_organization_id: conversation?.rollup_to_account?.id ?? '',
  }
}

type ConversationModalProps = {
  hideModal: () => void
  formType: FormType
  isAccount?: boolean
  objId: string
  accountId?: string
  accountName?: string
  conversation?: InteractionWithRollupsDataFragment
  resourceName?: string
  defaultContactMethod?: InteractionType
  hideContactMethod?: boolean
  hideRollups?: boolean
}

type CreateForm = CreateInteractionMutationVariables['form']
type UpdateForm = UpdateInteractionMutationVariables['form']

type Shared<A, B> = {
  [K in keyof A & keyof B]: A[K] extends B[K] ? K : never
}

type SharedFormProperties = {
  [K in Shared<CreateForm, UpdateForm>[keyof CreateForm & keyof UpdateForm]]: CreateForm[K]
}

export const ConversationModal: React.FC<ConversationModalProps> = ({
  hideModal,
  formType,
  isAccount,
  objId, // may be an account id or an opportunity id
  accountId, // in the context of an opportunity, this is the associated account id
  conversation,
  accountName,
  resourceName,
  defaultContactMethod,
  hideContactMethod,
  hideRollups,
}) => {
  const refetchQueries = [
    {
      query: isAccount ? AccountInteractionsDocument : OpportunityInteractionsDocument,
      variables: { id: objId, groupFilter: InteractionFilter.All },
    },
    { query: AllTodosDocument },
  ]

  const [createMutation, { loading: createLoading }] = useCreateInteractionMutation({
    refetchQueries,
    onCompleted: hideModal,
  })

  const [updateMutation, { loading: updateLoading }] = useUpdateInteractionMutation({
    refetchQueries,
    onCompleted: hideModal,
  })

  const onSubmit = (values: FormShape) => {
    const coreData: SharedFormProperties = {
      notes: values.communication!.text,
      tokens: values.communication!.tokens as InteractionTokenInput[],
      tagged_value: JSON.stringify(values.communication!.json),
      contact_method: values.contact_method!,
      rollup_from_id: values?.roll_up_from_account?.value ?? '',
      rollup_to_id: values?.roll_up_to_organization_id || undefined,
    }

    if (formType === 'create') {
      const variables: CreateInteractionMutationVariables = {
        form: {
          ...coreData,
          object_type: isAccount ? InteractionLogObjectType.Account : InteractionLogObjectType.TradeIntent,
          object_id: objId,
          postToSlackChannel: values.share_on_slack ? values.slack_channel : null,
        },
      }

      createMutation({ variables })
    } else {
      const variables: UpdateInteractionMutationVariables = {
        form: {
          ...coreData,
          id: values.id,
        },
      }

      updateMutation({ variables })
    }
  }

  const initialValues = createInitialValues(formType, {
    isAccount,
    conversation,
    objId,
    accountId,
    accountName,
    defaultContactMethod,
  })

  return (
    <FormModal open hideModal={hideModal} title={`${titleCase(formType)}: ${resourceName || 'Conversation'}`}>
      <Form<FormShape> onSubmit={onSubmit} validate={validateForm} initialValues={initialValues}>
        {formProps => (
          <ConversationForm
            hideContactMethod={hideContactMethod}
            hideRollups={hideRollups}
            resourceName={resourceName}
            accountId={accountId || objId}
            loading={createLoading || updateLoading}
            formType={formType}
            {...formProps}
          />
        )}
      </Form>
    </FormModal>
  )
}
